import { useState, useEffect } from 'react';
import { CaseSubscriptionApi } from '@api/case-subscription/caseSubscription';
import { CasePlan } from '@app/types/payment/subscription';

interface UseCaseSubscriptionPlansReturn {
  plans: CasePlan[];
  trialPlan: CasePlan | undefined;
  paidPlans: CasePlan[];
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

/**
 * Hook to fetch and manage case subscription plans
 * Automatically separates trial and paid plans
 */
export const useCaseSubscriptionPlans = (): UseCaseSubscriptionPlansReturn => {
  const [plans, setPlans] = useState<CasePlan[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchPlans = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await CaseSubscriptionApi.getAvailablePlans();
      setPlans(data || []);
    } catch (err) {
      console.error('Failed to fetch subscription plans:', err);
      setError('Failed to load subscription plans. Please try again.');
      setPlans([]); // Set empty array on error
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPlans();
  }, []);

  // Separate trial and paid plans - safely handle undefined/null
  const trialPlan = plans?.find((p) => p.is_trial);
  const paidPlans = plans?.filter((p) => !p.is_trial) || [];

  return {
    plans,
    trialPlan,
    paidPlans,
    loading,
    error,
    refetch: fetchPlans,
  };
};
